import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { LineItem } from 'shared/types/wishlist/LineItem';
import Dropdown from 'components/commercetools-ui/atoms/dropdown';
import Modal from 'components/commercetools-ui/atoms/modal';
import Typography from 'components/commercetools-ui/atoms/typography';
import TrustBox from 'components/commercetools-ui/organisms/trustpilot';
import WishlistButton from 'components/commercetools-ui/organisms/wishlist/components/wishlist-button';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import useClassNames from 'helpers/hooks/useClassNames';
import useLocale from 'helpers/hooks/useLocale';
import { StringHelpers } from 'helpers/stringHelpers';
import CustomfitOptions from './customfit-options';
import { ProductDetailsProps } from '..';
import { discardRepeatedValues } from '../helpers/discardRepeatedValues';
import VariantOptions from './variantOptions';
import { Attributes, Money, Variant } from 'shared/types/product';
import { CartDiscount, promotionCategory, SelectedAttributes } from '../types';
import CustomfitVariant from './customfit-variant';
import { useCookies } from 'react-cookie';
import { getCurrencyCookie } from 'helpers/utils/getCurrencyCookie';
import { Price } from 'shared/types/product/Variant';
import sale from 'public/locales/en/product.json';
import Link from 'components/commercetools-ui/atoms/link';
import { DataLayerHelpers } from 'helpers/dataLayerHelpers';
import AccordionBtn from 'components/commercetools-ui/atoms/accordion';
import { PreviewContent, productSlug, TableSet, TermsAndCondition } from 'shared/types/product/Product';
import { useSearchParams } from 'next/navigation';
import CustomFitNew from './customfit-new';
import { SmartLink } from 'components/commercetools-ui/atoms/smart-link';
import PlayButton from 'public/images/Stroke.svg';
import ChevronDown from 'public/icons/ag-icons/chevron-down-trustpilot.svg';
import React from 'react';
import PersonalizedView from './personalised-golfballs-options';
import PlusIcon from 'public/icons/ag-icons/vector.svg';
import MinusIcon from 'public/icons/ag-icons/decrease-quantity-icon.svg';
import { getEstimatedDispatchDate } from '../helpers/getEstimatedDispatchDate';
import TermsContent from './termsAndCondition';
import { useFormat } from 'helpers/hooks/useFormat';

const typeMapping: Record<string, string> = {
  waistsize: 'waist',
  leglength: 'leg',
};

type ProductInformationProps = Omit<ProductDetailsProps, 'onAddToCart'> & {
  product?: any; // Replace `any` with the appropriate type for `product` if known
};
const ProductInformation: FC<ProductInformationProps> = ({
  product = {},
  variant,
  onChangeVariant,
  inModalVersion,
  handleScrollReviews,
  setIsCartDisable,
  setHoveredImage,
  setCustomFitPrice,
  customFitPrice,
  setQuantity,
  quantity,
  setPayloadAttributes,
  setDeliveryDate,
  deliveryDate,
  setSelectedSKUs,
  payloadAttributes,
  isCartDisable,
  selectedSKUs,
  breadCrumb,
  handleColorVariant,
  viewStandardFitOptions,
  trustBoxCss,
  allBadges,
  isDesktopSize,
  cartDiscountBadge,
  isMobile,
  selectedLocale = 'en-GB',
  StarImageComponent,
  starsAverage,
  numberOfReviews,
  colorVariant,
  setDiscountedMRPrices,
  discountedMRPrices,
  isPersonalized,
  personalisationBalls,
  minOrderQuantity,
  onPersonalisationChange,
  personalisationData,
}) => {
  const router = useRouter();
  const { ctLocale: locale } = useLocale();
  const [isSizeGuideModalOpen, setIsSizeGuideModalOpen] = useState(false);
  const sizeOverlayContentsRef = useRef<HTMLIFrameElement | null>(null);
  const variantValue = useSearchParams();
  const paramValue = variantValue.get('variant');
  const [variantSelected, setVariantSelected] = useState(false);
  // const [quantity, setQuantity] = useState<number>(1);
  const [trustPilotSkuId, setTrustPilotSkuId] = useState('');
  const [hasReviews, setHasReviews] = useState(false);
  const [cookies, setCookie] = useCookies(['locale_selection', 'frontastic-session']);
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>({
    colour: variant?.attributes?.colour || colorVariant?.attributes?.colour || '',
    size: variant?.attributes?.size || '',
    shoesize: variant?.attributes?.shoesize || '',
    waist: variant?.attributes?.waist || '',
    leg: variant?.attributes?.leg || '',
    hand: variant?.attributes?.hand || '',
  });
  const [price, setPrice] = useState<number>(0);
  const [discountedPrices, setDiscountedPrices] = useState<any>(null);
  const [customFitMRRP, setCustomFitMRRP] = useState<number | null>(null);
  const [fractionDigits, setFractionDigits] = useState<number>(2);
  const [currencyCode, setCurrencyCode] = useState<string>('GBP');
  const [trustpilotData, setTrustPilotData] = useState<any>();
  const [open, setOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState(variant?.attributes?.personalisedInkColour || '');
  const modalRef = useRef<HTMLDivElement>(null);
  // const minOrderQuantity = isPersonalized
  //   ? product?.variants?.[0]?.attributes && product?.variants?.[0]?.attributes?.personalisedMinQuantity || 2
  //   : 1;
  // const maxOrderQuantity = isPersonalized
  //   ? Number(getAttributeValue(product?.productAttributes, 'maxOrderQuantity')) || 10
  //   : 10;
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  // const [customFitPrice, setCustomFitPrice] = useState<number | null>(null);
  const originalOrder = ['colour', 'size', 'shoesize', 'waistsize', 'leglength', 'hand'];
  const path = usePathname();
  const tableSet = product?.sizeGuide?.tableSet as TableSet[];
  const sizeData = [
    { uk: '4', us: '6', eu: '37', mm: '240' },
    { uk: '4.5', us: '6.5', eu: '37.5', mm: '245' },
    { uk: '5', us: '7', eu: '38', mm: '249' },
    { uk: '5.5', us: '7.5', eu: '38.5', mm: '253' },
    { uk: '6', us: '8', eu: '39', mm: '257' },
    { uk: '6.5', us: '8.5', eu: '40.5', mm: '262' },
    { uk: '7', us: '9', eu: '41', mm: '266' },
  ];
  const localeSelection = getCurrencyCookie('locale_selection');
  const currency: string | null = localeSelection?.currency || null;
  const promotionCategory = product?.categories as promotionCategory[] | undefined;

  const saleCategory = promotionCategory?.some((i: promotionCategory) => i?.slug == sale.sale.slug);
  const agExclusive = promotionCategory?.some((i: promotionCategory) => i?.slug == sale['AG-Exclusive'].slug);
  const newArrivals = promotionCategory?.find((i: promotionCategory) => i?.name === 'New Arrivals');
  const discountCategory = promotionCategory?.find(
    (category: promotionCategory) => category.name && /\d+%/.test(category.name),
  );
  const isIronsCategory =
    promotionCategory?.some((i: promotionCategory) => i?.name?.toLowerCase().includes('irons')) &&
    !!product?.customFitDetails;
  const discountPercent = discountCategory?.name.match(/(\d+)%/)?.[1];

  const discountPromo = discountPercent ? `+Extra ${discountPercent}% Off At Checkout` : '';

  const discountUrl = discountCategory?.slug ?? '';
  const searchParams = useSearchParams();
  const variantCode = searchParams.get('variantCode');
  const termsAndCondition = (product?.personalisation?.termsAndCondition as TermsAndCondition) || null;
  const perviewContent = (product?.personalisation?.previewContent as PreviewContent) || null;
  // As of now Commented : currently we are getting price from standalonePrice in attributes
  // const price = variant?.prices?.filter(
  //   (i: Price) => i?.currencyCode === cookies?.locale_selection?.currency,
  // ) as Price[];
  // const standalonePrice = variant?.attributes?.standalonePrice ? JSON.parse(variant.attributes.standalonePrice) : {};

  // // Get price object based on locale selection
  // const priceObj = standalonePrice?.[cookies?.locale_selection?.currency === 'GBP' ? 'en-GB' : 'en-EU']?.value;

  const BrandLogo = `https://${product?.brandLogo?.BrandImage?.endpoint}.a.bigcontent.io/v1/static/${product?.brandLogo?.BrandImage?.name}`;

  const currentSku = product?.slug;
  // As of now Commented
  // const discountPercentage =
  // variant.discountedPrice &&
  // (((price?.[0]?.centAmount as number) - (variant.discountedPrice.centAmount as number)) /
  //   (price?.[0]?.centAmount as number)) *
  //   100;
  // const discountPercentage =
  //   variant.discountedPrice &&
  //   variant.discountedPrice &&
  //   ((price - (variant.discountedPrice.centAmount as number)) / price) * 100;
  const discountPercentage = useMemo(() => {
    if (!discountedPrices || !discountedPrices?.mrrp?.centAmount || !price) {
      return null; // Return null if required data is missing
    }

    const mrrp = discountedPrices?.mrrp?.centAmount;
    const wasPrice = discountedPrices?.wasPrice?.centAmount;
    if (mrrp <= 0 || price <= 0 || mrrp === price || mrrp < price) {
      return 0; // No discount if prices are invalid or no actual discount
    }

    const discount = ((mrrp - price) / mrrp) * 100;
    return Math.round(discount); // Round to the nearest integer
  }, [discountedPrices]);
  const getRanking = (badge: any) => Number(badge?.ranking ?? badge?.ranking ?? 99);
  const sortedBadges = useMemo(
    () => allBadges?.sort((a: any, b: any) => getRanking(a) - getRanking(b)),
    [allBadges, isDesktopSize, isMobile, selectedLocale],
  );

  // {*The below code for addToWishList is movied to product-details\index.tsx file so comminthing the code here*}
  // const productToWishlistLineItem = useMemo<LineItem>(() => {
  //     return {
  //         lineItemId: product?.productId ?? '',
  //         productId: product?.productId,
  //         name: product?.name,
  //         count: 1,
  //         variant: variant,
  //         addedAt: new Date(),
  //         _url: product?._url,
  //     };
  // }, [product, variant]);

  const titleClassName = useClassNames(['break-normal mb-0', { 'md:text-24': !inModalVersion }]);
  const priceClassName = useClassNames([
    'block leading-loose',
    { 'line-through text-[#4B4B4B] font-normal leading-4 font-body text-14': !!discountPercentage },
    { 'text-14 font-bold leading-6 font-body': !inModalVersion },
    { 'text-22': !!inModalVersion && !discountPercentage },
    { 'text-14': !!inModalVersion && !!discountPercentage },
  ]);
  const discountedPriceClassName = useClassNames([
    'block font-bold font-body leading-6 text-[#EB0E0E] text-20',
    { 'text-20': !inModalVersion },
  ]);
  const getFallbackOptionOrder = (variants: { sku: string; optionsOrder: string[] }[]) => {
    // Sort variants by the length of their optionsOrder in descending order
    const sortedVariants = variants?.sort((a, b) => b?.optionsOrder?.length - a?.optionsOrder?.length);

    const correctOption = sortedVariants?.[0]?.optionsOrder || [];

    const finalOption: string[] = [];

    // Compare each variant's optionsOrder with the current correctOption
    sortedVariants?.forEach((variant) => {
      correctOption?.forEach((option) => {
        if (!finalOption?.includes(option) && variant?.optionsOrder?.includes(option)) {
          finalOption?.push(option);
        }
      });
    });

    return finalOption;
  };

  const optionsOrder =
    (variant?.attributes?.optionsOrder ?? []).length > 0
      ? variant?.attributes?.optionsOrder
      : getFallbackOptionOrder(
          product?.variants?.map((v: Variant) => ({
            sku: v?.sku,
            optionsOrder: v?.attributes?.optionsOrder || [],
          })) || [],
        );

  const cartDiscount = useMemo(() => {
    try {
      return (
        (JSON.parse(
          (variant?.attributes?.cartDiscount ?? product?.variants?.[0]?.attributes?.cartDiscount) as string,
        ) as CartDiscount[]) || []
      );
    } catch {
      return [];
    }
  }, [variant]);
  const parentCategoryLink = product?.breadCrumb;

  const sortedOptionsOrder = useMemo(
    () =>
      optionsOrder
        ?.filter((attribute: string) => attribute !== 'model' && attribute !== 'width')
        ?.sort((a: string, b: string) => {
          const indexA = originalOrder?.indexOf(a);
          const indexB = originalOrder?.indexOf(b);

          if (indexA === -1) return 1;
          if (indexB === -1) return -1;

          return indexA - indexB;
        }),
    [optionsOrder, originalOrder],
  );
  const customFitVariant = product?.variants?.find((variant: Variant) => variant?.attributes?.isCustomFit === true);
  const isGPSCategory = !!product?.categories?.some((category: { name: string }) => category?.name === 'GPS');
  const isGolfClubProducts = product?.categories?.some((category: any) =>
    /golf-clubs|clubs|CLUBS|GOLF-CLUBS/.test(category?.slug),
  );
  const changeRoute = (updatedSku: string) => {
    if (updatedSku) {
      const variant = product?.variants?.find((variant: any) => variant?.sku === updatedSku);
      const variantCodeToURL = variant?.attributes?.variantCode || '';
      const variantSkuToURL = variant?.sku || '';
      const currentParams = new URLSearchParams(window.location.search);
      const currentVariantCode = currentParams.get('variantCode') || '';
      const currentSku = currentParams.get('sku') || '';

      const validOptionsOrder = sortedOptionsOrder?.filter((attribute) => {
        const mappedAttribute = typeMapping?.[attribute] || attribute;
        return product?.variants?.some((variant: Variant) => variant?.attributes?.[mappedAttribute]);
      });
      const mappedAttributes = validOptionsOrder?.map((attr: string) => typeMapping[attr] || attr);
      // Helper: merge new params into current query string while preserving existing utm_* params
      const pushMergedParams = (overrides: Record<string, string | undefined>) => {
        try {
          const url = new URL(window.location.href);
          const existing = new URLSearchParams(url.search);

          // Apply overrides to a copy of existing params
          const merged = new URLSearchParams();

          // First, set variant params from overrides (if provided) or existing
          const variantKeys = ['variantCode', 'sku'];
          variantKeys.forEach((k) => {
            const v = overrides[k] ?? existing.get(k) ?? undefined;
            if (v === undefined || v === null || v === '') {
              // ensure removed if override explicitly clears
              // do nothing (won't be added)
            } else {
              merged.set(k, v as string);
            }
          });

          // Next, preserve all existing params (unless explicitly overridden/removed).
          // We'll append them after the variant keys. Overrides take precedence;
          // explicit null/empty string values remove the key.
          const allKeys = new Set<string>([...Array.from(existing.keys()), ...Object.keys(overrides)]);
          allKeys.forEach((k) => {
            if (variantKeys.includes(k)) return; // already handled

            const overrideVal = overrides[k];
            if (overrideVal === undefined) {
              const existingVal = existing.get(k);
              if (existingVal !== null) {
                merged.set(k, existingVal);
              }
            } else if (overrideVal === null || overrideVal === '') {
              // explicit removal: do nothing
            } else {
              merged.set(k, overrideVal as string);
            }
          });

          const qs = merged.toString();
          const newPath = qs ? `${url.pathname}?${qs}` : url.pathname;
          window.history.pushState(null, '', newPath);
        } catch (e) {
          // Fallback to original behaviour if URL constructor fails
          // Build query string prioritizing variant keys, then utm_*, then others
          const paramsObj: Record<string, string> = {};
          const variantKeys = ['variantCode', 'sku'];
          variantKeys.forEach((k) => {
            const v = overrides[k] as string | undefined;
            if (v) paramsObj[k] = v;
          });

          // Attach all existing params (except variant keys which are already handled)
          const raw = window.location.search ? window.location.search.replace(/^[?]/, '') : '';
          const existingPairs = raw
            .split('&')
            .filter(Boolean)
            .map((p) => p.split('=')) as string[][];
          existingPairs.forEach(([k, v]) => {
            if (!k) return;
            if (variantKeys.includes(k)) return;
            if (!(k in paramsObj)) paramsObj[k] = v ?? '';
          });

          // Add other overrides (non-variant)
          Object.entries(overrides)
            .filter(([k]) => !variantKeys.includes(k))
            .forEach(([k, v]) => {
              if (v) paramsObj[k] = v as string;
              else if (v === null || v === '') delete paramsObj[k];
            });

          const qs = Object.entries(paramsObj)
            .map(([k, v]) => `${k}=${encodeURIComponent(v as string)}`)
            .join('&');
          const newPath = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
          window.history.pushState(null, '', newPath);
        }
      };

      // Check if only color is present in validOptionsOrder
      const isOnlyColorAttribute =
        validOptionsOrder?.length === 1 && (validOptionsOrder?.[0] === 'colour' || validOptionsOrder?.[0] === 'color');
      // If only color is present, update both variantCode and sku directly
      if (isOnlyColorAttribute && !isPersonalized) {
        if (variantCodeToURL && variantSkuToURL) {
          pushMergedParams({ variantCode: variantCodeToURL, sku: variantSkuToURL });
        }
        return;
      }
      // // Check if only color is selected among all available attributes in optionsOrder
      const isOnlyColorSelected = () => {
        // const validOptionsOrder = sortedOptionsOrder?.filter((attribute: any) => attribute !== 'model' && attribute !== 'width');

        // Map waistsize/leglength to waist/leg for comparison
        // const mappedAttributes = validOptionsOrder?.map((attr: string) => {
        //   const mapped = typeMapping[attr] || attr;
        //   return mapped;
        // });

        // Check if only colour is selected and others are empty/unselected
        const selectedCount =
          mappedAttributes?.filter((attr: string) => {
            const value = (selectedAttributes as any)[attr];
            return value && value.trim() !== '';
          }).length || 0;

        const isColorSelected = selectedAttributes?.colour && selectedAttributes?.colour.trim() !== '';

        return isColorSelected && selectedCount === 1;
      };

      const hasMultipleAttributesSelected = () => {
        // const validOptionsOrder = sortedOptionsOrder?.filter((attribute: any) => attribute !== 'model' && attribute !== 'width');

        // const mappedAttributes = validOptionsOrder?.map((attr: string) => {
        //   const mapped = typeMapping[attr] || attr;
        //   return mapped;
        // });

        const selectedCount =
          mappedAttributes?.filter((attr: string) => {
            const value = (selectedAttributes as any)[attr];
            return value && value.trim() !== '';
          }).length || 0;

        return selectedCount > 1;
      };
      // --- PERSONALISATION LOGIC ---
      // If isPersonalized and onPersonalisationChange?.linkColour exists, treat as multiple attributes selected
      if (isPersonalized && selectedColor) {
        if (variantCodeToURL && variantSkuToURL) {
          if (variantCodeToURL !== currentVariantCode || variantSkuToURL !== currentSku) {
            pushMergedParams({ variantCode: variantCodeToURL, sku: variantSkuToURL });
          }
        }
        return;
      }

      // Case 1: Only color is selected - update only variantCode
      if (isOnlyColorSelected()) {
        if (variantCodeToURL && variantCodeToURL !== currentVariantCode) {
          pushMergedParams({ variantCode: variantCodeToURL });
        }
      }
      // Case 2: Multiple attributes selected - update both variantCode and sku
      else if (hasMultipleAttributesSelected()) {
        if (variantCodeToURL && variantSkuToURL) {
          if (variantCodeToURL !== currentVariantCode || variantSkuToURL !== currentSku) {
            pushMergedParams({ variantCode: variantCodeToURL, sku: variantSkuToURL });
          }
        }
      } else if (
        (variantCodeToURL && variantCodeToURL !== currentVariantCode) ||
        (variantSkuToURL && variantSkuToURL !== currentSku)
      ) {
        pushMergedParams({ variantCode: variantCodeToURL, sku: variantSkuToURL });
      }

      // Case 3: No attributes selected or variant has no variantCode - clear URL params
      else if (!variantCodeToURL && (currentVariantCode || currentSku)) {
        // Remove variantCode and sku but preserve other params like utm_*
        pushMergedParams({ variantCode: undefined, sku: undefined });
      }
    }
  };

  // useEffect(() => {
  //   if ((sortedOptionsOrder as any)?.length > 0 && product?.variants?.length > 0) {
  //     const updatedAttributes: SelectedAttributes = { ...selectedAttributes };

  //     (sortedOptionsOrder as any).forEach((attribute: any) => {
  //       const uniqueValues = [
  //         ...new Set(product.variants.map((variant: Variant) => variant.attributes?.[attribute]).filter(Boolean)),
  //       ];

  //       // Only update if the attribute is not already set by the user
  //       if (!(selectedAttributes as any)[attribute] && uniqueValues.length === 1) {
  //         (updatedAttributes as any)[attribute] = uniqueValues[0]; // Auto-select if only one unique value exists
  //       }
  //     });

  //     // Update selectedAttributes only if there are changes
  //     if (JSON.stringify(updatedAttributes) !== JSON.stringify(selectedAttributes)) {
  //       setSelectedAttributes(updatedAttributes);
  //     }
  //   }
  // }, [sortedOptionsOrder, product?.variants, selectedAttributes]);

  useEffect(() => {
    if (product?.variants) {
      const skus = product?.variants
        .filter((v: Variant) => v?.sku)
        .map((v: Variant) => StringHelpers?.removeWhiteSpace(v?.sku))
        .join(',');
      if (skus) {
        setTrustPilotSkuId(skus);
        localStorage?.setItem('currentlySelectedVariants', skus);
      }
    }
  }, [product]);

  // useEffect(() => {
  //   const selectedCurrency = cookies?.locale_selection?.currency;
  //   if (
  //     selectedCurrency &&
  //     product?.customFitDetails?.CF_Price &&
  //     selectedCurrency in product.customFitDetails.CF_Price
  //   ) {
  //     setCustomFitPrice(
  //       product.customFitDetails.CF_Price[selectedCurrency as keyof typeof product.customFitDetails.CF_Price],
  //     );
  //   } else {
  //     setCustomFitPrice(null);
  //   }
  // }, []);

  useEffect(() => {
    // if (variant?.attributes?.standalonePrice || product?.variants[0]?.attributes?.standalonePrice) {
    try {
      const standalonePrice = variant?.attributes?.standalonePrice
        ? JSON.parse(variant?.attributes?.standalonePrice)
        : product?.variants[0].attributes.standalonePrice
        ? JSON.parse(product?.variants[0].attributes.standalonePrice)
        : {};
      const selectedPrice = standalonePrice?.[cookies?.locale_selection?.currency === 'GBP' ? 'en-GB' : 'en-EU']?.value;
      const discountedPrices =
        standalonePrice?.[cookies?.locale_selection?.currency === 'GBP' ? 'en-GB' : 'en-EU']?.custom || null;
      if (selectedPrice) {
        setPrice(selectedPrice.centAmount);
        setFractionDigits(selectedPrice.fractionDigits ?? 2);
        setCurrencyCode(selectedPrice.currencyCode);
      }
      if (discountedPrices) {
        setDiscountedPrices(discountedPrices);
      } else {
        setDiscountedPrices(null);
      }
      if (discountedPrices?.mrrp?.centAmount) {
        setCustomFitMRRP(discountedPrices.mrrp.centAmount / Math.pow(10, 2));
        setDiscountedMRPrices(discountedPrices.mrrp.centAmount / Math.pow(10, 2));
      } else {
        setCustomFitMRRP(null);
        setDiscountedMRPrices(null);
      }
    } catch (error) {
      console.error('Error parsing standalonePrice:', error);
    }
    // }
  }, [variant, cookies?.locale_selection?.currency]);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const hasAllNonEmptyValues =
      payloadAttributes.length > 0 &&
      payloadAttributes.every((attr: any) => {
        const value = attr.value;
        return Array.isArray(value) ? value.length > 0 : value !== '';
      });

    if (hasAllNonEmptyValues) {
      const dataLayerProductUpdatePush = new DataLayerHelpers(path, locale, variant ?? product?.variants?.[0]);
      dataLayerProductUpdatePush.pushProductViewUpdateToDataLayer(
        product,
        variant ?? product?.variants?.[0],
        cookies?.locale_selection?.currency,
        selectedAttributes,
        payloadAttributes,
        isGolfClubProducts,
        customFitVariant,
      );
    }
  }, [JSON.stringify(payloadAttributes.map((attr: any) => attr.value))]);
  const [previousVariant, setPreviousVariant] = useState<string | null>(null);

  useEffect(() => {
    if (variantSelected && !isCartDisable && (previousVariant as any) !== variantSelected) {
      setPreviousVariant(variantSelected as any);

      const hasNonEmptySelectedAttributes = Object.values(selectedAttributes || {}).some(
        (value) => value.trim() !== '',
      );

      if (hasNonEmptySelectedAttributes) {
        const dataLayerProductUpdatePush = new DataLayerHelpers(path, locale, variant ?? product?.variants?.[0]);
        dataLayerProductUpdatePush.pushProductViewUpdateToDataLayer(
          product,
          variant ?? product?.variants?.[0],
          cookies?.locale_selection?.currency,
          selectedAttributes,
          payloadAttributes,
          isGolfClubProducts,
          customFitVariant,
        );
      }
    }
  }, [
    variantSelected,
    isCartDisable,
    selectedAttributes,
    path,
    locale,
    variant,
    cookies,
    payloadAttributes,
    isGolfClubProducts,
    customFitVariant,
    previousVariant,
  ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <div className="flex flex-col gap-y-24">
      <div className="hidden sm:flex sm:flex-col sm:gap-y-24">
        {/* <div className="flex flex-col gap-y-24"> */}
        {product?.brandLogo && (
          <div className="w-[100%] max-w-[150px]">
            <img src={BrandLogo} className="w-[100%]" />
          </div>
        )}
        <Typography as="h1" className={`${titleClassName} text-start font-semibold leading-8`}>
          {product?.name + (selectedAttributes?.colour ? ' - ' + selectedAttributes?.colour : '')}
        </Typography>
        {StarImageComponent && numberOfReviews !== 0 && (
          <div
            onClick={() => handleScrollReviews()}
            // style={{
            //   position: 'relative',
            //   // pointerEvents: hasReviews ? 'auto' : 'none',
            //   // display: hasReviews ? 'block' : 'none',
            // }}
          >
            {/* <div className="mb-10 py-7" style={{ pointerEvents: 'none' }}>
            <TrustBox id={variant?.sku} trustBoxCss={trustBoxCss} showVertical={false} onWidgetLoad={setHasReviews} />
          </div> */}
            <div className="flex justify-start">
              <div className="inline-flex cursor-pointer items-center gap-6 align-baseline">
                {StarImageComponent && <StarImageComponent />}
                {numberOfReviews !== 0 && (
                  <div className="flex items-center gap-8">
                    <span className="text-sm font-normal text-[#1E1E1E]">{numberOfReviews} reviews</span>

                    <ChevronDown />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {sortedBadges?.length > 0 && (
          <div className="flex gap-x-12">
            {sortedBadges?.slice(0, 3)?.map((badge: any, idx: number) => {
              const containerPosition =
                (isDesktopSize ? badge?.visibility?.desktop?.position : badge?.visibility?.mobile?.position) ||
                'center';
              const styles = badge?.styles;
              const isGradient = styles?.backgroundGradient?.startsWith('linear-gradient');
              const iconPosition = badge?.icon?.position || 'left';
              const iconType = badge?.icon?.positioning?.type || 'relative';
              const shouldUseLineBreak = isDesktopSize ? styles?.manualLineBreakDesktop : styles?.manualLineBreakMobile;
              const iconAsset = badge?.icon?.asset?.[selectedLocale];
              const iconSize = isDesktopSize
                ? Math.min(badge?.icon?.iconSizeDesktopPx ?? 0, 22)
                : Math.min(badge?.icon?.iconSizeMobilePx ?? 0, 18);
              const iconPositioning = badge?.icon?.positioning?.[isDesktopSize ? 'desktop' : 'mobile'] || {
                top: 0,
                left: 0,
              };
              const iconStyle: React.CSSProperties = {
                position: iconType,
                top: iconPositioning.top ?? 0,
                left: iconPositioning.left ?? 0,
                width: badge?.icon?.sizePx,
                height: badge?.icon?.sizePx,
                ...(iconPosition === 'left' && { marginRight: badge?.icon?.spacingPx ?? 0 }),
                ...(iconPosition === 'right' && { marginLeft: badge?.icon?.spacingPx ?? 0 }),
              };
              const selfClass =
                containerPosition === 'center'
                  ? 'self-center'
                  : containerPosition === 'right'
                  ? 'self-end'
                  : 'self-start';

              const iconElement = iconAsset && (
                <img
                  src={`https://${iconAsset.endpoint}.a.bigcontent.io/v1/static/${iconAsset.name}`}
                  alt=""
                  width={iconSize}
                  height={iconSize}
                  style={iconStyle}
                />
              );

              const label = badge?.label?.[selectedLocale] || '';
              return (
                <span
                  key={badge?.id || badge?.badgeText || idx}
                  className={`inline-flex w-fit items-center ${selfClass}`}
                  style={{
                    ...(isGradient
                      ? { backgroundImage: styles?.backgroundGradient }
                      : { backgroundColor: styles?.backgroundColor }),
                    color: styles?.textColor,
                    fontSize: `${!isDesktopSize ? styles?.fontSizeMobilePx : styles?.fontSizeDesktopPx}px`,
                    fontWeight: styles?.fontWeight,
                    padding: !isDesktopSize ? styles?.paddingMobile : styles?.paddingDesktop,
                    borderRadius: styles?.borderRadius ? `${styles?.borderRadius}px` : undefined,
                    letterSpacing: styles?.letterSpacing ? `${styles?.letterSpacing}px` : undefined,
                    minHeight: `${!isDesktopSize ? styles?.minHeightMobilePx : styles?.minHeightDesktopPx}px`,
                    maxHeight: `${!isDesktopSize ? styles?.maxHeightMobilePx : styles?.maxHeightDesktopPx}px`,
                    gap: `${!isDesktopSize ? styles?.columnGapMobilePx : styles?.columnGapDesktopPx}px`,
                    borderWidth: `${styles?.borderThickness || 0}px`,
                    borderColor: styles?.borderColor,
                  }}
                >
                  {iconPosition === 'left' && iconElement}
                  {shouldUseLineBreak ? (
                    <p className="text-center" dangerouslySetInnerHTML={{ __html: label }} />
                  ) : (
                    <p className="text-center">{label?.replace(/<[^>]*>/g, '')}</p>
                  )}{' '}
                  {iconPosition === 'right' && iconElement}
                </span>
              );
            })}
          </div>
        )}
        {discountPromo ||
          (cartDiscount?.length > 0 && (
            <div className="flex flex-col gap-y-12">
              {discountPromo && (
                <a href={`/${discountUrl}`}>
                  <div className="inline-flex w-full cursor-pointer items-center justify-center bg-green-100 p-2 px-4 py-2 text-16 font-medium leading-7">
                    {discountPromo}
                  </div>
                </a>
              )}

              {cartDiscount?.length > 0 && (
                <div className={`flex w-full flex-col gap-y-12 text-center`}>
                  {cartDiscount?.map((i: CartDiscount, index: number) => {
                    const desc = i?.description?.[cookies?.locale_selection?.currency === 'GBP' ? 'en-GB' : 'en-EU'];
                    if (desc) {
                      return (
                        <SmartLink
                          key={index}
                          href={`${breadCrumb?.[breadCrumb?.length - 1]?.href}shop-by/offer/${
                            i.freeGiftName?.toLocaleLowerCase()
                              ? i.freeGiftName?.toLocaleLowerCase()?.replace(/\s+/g, '_')?.replace('€', '€')
                              : desc
                                  ?.replace(/\s+/g, '_')
                                  ?.replace('€', '€')
                                  ?.replaceAll('%', 'percentage')
                                  ?.replace(',', '')
                          }`}
                        >
                          <div
                            style={{
                              backgroundColor: cartDiscountBadge?.styles?.backgroundColor,
                              padding: !isDesktopSize
                                ? cartDiscountBadge?.styles?.paddingMobile
                                : cartDiscountBadge?.styles?.paddingDesktop,
                              borderRadius: cartDiscountBadge?.styles?.borderRadius,
                              minHeight: !isDesktopSize
                                ? `${cartDiscountBadge?.styles?.minHeightMobilePx}px`
                                : `${cartDiscountBadge?.styles?.minHeightDesktopPx}px`,
                              maxHeight: !isDesktopSize
                                ? `${cartDiscountBadge?.styles?.maxHeightMobilePx}px`
                                : `${cartDiscountBadge?.styles?.maxHeightDesktopPx}px`,
                            }}
                            className={`flex h-full min-h-[40px] w-full min-w-[86px] cursor-pointer flex-col items-center justify-center bg-ag_color-custom_green px-4 py-8 font-body text-14 font-medium capitalize text-black md:text-16 lg:text-18`}
                          >
                            {desc?.split(',')?.map((part, idx, arr) => (
                              <span
                                key={idx}
                                style={{
                                  color: cartDiscountBadge?.styles?.textColor,
                                  fontSize: !isDesktopSize
                                    ? cartDiscountBadge?.styles?.fontSizeMobilePx
                                    : cartDiscountBadge?.styles?.fontSizeDesktopPx,
                                  fontWeight: cartDiscountBadge?.styles?.fontWeight,
                                  letterSpacing: cartDiscountBadge?.styles?.letterSpacing,
                                }}
                              >
                                {part?.trim()}
                                {idx < arr?.length - 1 && <br />}
                              </span>
                            ))}
                          </div>
                        </SmartLink>
                      );
                    }
                    return null;
                  })}
                </div>
              )}
            </div>
          ))}

        {Array.isArray(viewStandardFitOptions) &&
          Array.isArray(product?.popupData?.productSlug) &&
          product?.popupData?.productSlug?.some((item: productSlug) => item?.slug === product?.slug) && (
            <div className="View-Standard-options">
              {viewStandardFitOptions?.map((optionBlock: any, idx: number) => {
                const isCustomFit = product?.variants?.[0]?.attributes?.isCustomFit;

                return Array.isArray(optionBlock?.buttons)
                  ? optionBlock?.buttons
                      ?.filter((btn: any) => btn?.isCustomFit === isCustomFit)
                      ?.map((btn: any, btnIdx: number) => {
                        const matchingSlug = product?.popupData?.productSlug?.find(
                          (item: productSlug) => item?.slug === product?.slug,
                        );

                        return (
                          <SmartLink showLoader={false} href={matchingSlug?.slugLink || ''} key={`${idx}-${btnIdx}`}>
                            <div className="relative w-fit">
                              <button
                                className="flex h-[32px] min-w-[216px] items-center justify-between rounded-[4px] pl-10 pr-10 font-body font-medium"
                                style={{
                                  backgroundColor: btn?.buttonColour,
                                  color: btn?.TextColour,
                                  fontSize: btn?.TextFontSize,
                                }}
                              >
                                <span className="w-full">{btn?.buttonText || 'View Standard Fit options'}</span>
                                <span className="ml-10">
                                  <PlayButton />
                                </span>
                              </button>
                            </div>
                          </SmartLink>
                        );
                      })
                  : null;
              })}
            </div>
          )}
      </div>
      <div className="flex flex-col items-start gap-32 self-stretch">
        {customFitVariant || isGolfClubProducts ? (
          <div className="mt-[1rem] w-full">
            {product?.customFitDetails ? (
              <CustomFitNew
                product={product}
                // variant={customFitVariant}
                setIsCartDisable={setIsCartDisable}
                setCustomFitPrice={setCustomFitPrice}
                customFitPrice={customFitPrice}
                scrollable={true}
                setPayloadAttributes={setPayloadAttributes}
                setDeliveryDate={setDeliveryDate}
                deliveryDate={deliveryDate}
                setSelectedSKUs={setSelectedSKUs}
                setQuantity={setQuantity}
                currency={currency}
                selectedSKUs={selectedSKUs}
                setDiscountedMRPrices={setDiscountedMRPrices}
                baseMPRPrice={customFitMRRP}
                showSizeGuide={() => {
                  setIsSizeGuideModalOpen(true);
                }}
              />
            ) : (
              // <CustomfitVariant
              //   product={product}
              //   // variant={customFitVariant}
              //   setIsCartDisable={setIsCartDisable}
              //   setCustomFitPrice={setCustomFitPrice}
              //   customFitPrice={customFitPrice}
              //   scrollable={true}
              //   setPayloadAttributes={setPayloadAttributes}
              //   setSelectedSKUs={setSelectedSKUs}
              //   setQuantity={setQuantity}
              //   currency={currency}
              // />
              <CustomfitOptions
                product={product}
                variant={variant}
                setIsCartDisable={setIsCartDisable}
                onChangeVariant={onChangeVariant}
                changeRoute={changeRoute}
                showSizeGuide={() => {
                  setIsSizeGuideModalOpen(true);
                }}
              />
            )}
          </div>
        ) : isPersonalized ? (
          <PersonalizedView
            perviewContent={perviewContent}
            product={product}
            setIsCartDisable={setIsCartDisable}
            minOrderQuantity={minOrderQuantity}
            quantity={quantity}
            personalisationBalls={personalisationBalls}
            colorVariant={colorVariant}
            onPersonalisationChange={onPersonalisationChange}
            handleColorVariant={handleColorVariant}
            setHoveredImage={setHoveredImage}
            selectedAttributes={selectedAttributes}
            setSelectedAttributes={setSelectedAttributes}
            onChangeVariant={onChangeVariant}
            changeRoute={changeRoute}
            setSelectedColor={setSelectedColor}
            selectedColor={selectedColor}
            variant={variant}
          />
        ) : (
          <div className="relative flex flex-col gap-16 py-5">
            {sortedOptionsOrder?.map((type: string, index: number) => {
              const attributeType = typeMapping[type] || type;
              return (
                <VariantOptions
                  key={index}
                  type={type}
                  showSizeGuide={() => {
                    setIsSizeGuideModalOpen(true);
                  }}
                  variants={discardRepeatedValues(product?.variants, attributeType) ?? []}
                  product={product}
                  selectedAttributes={selectedAttributes}
                  setSelectedAttributes={setSelectedAttributes}
                  optionsOrder={sortedOptionsOrder}
                  changeRoute={changeRoute}
                  onChangeVariant={onChangeVariant}
                  isGPSCategory={isGPSCategory}
                  setHoveredImage={setHoveredImage}
                  setVariantSelected={setVariantSelected}
                  variantSelected={variantSelected}
                  tableSet={tableSet}
                  setIsCartDisable={setIsCartDisable}
                  handleColorVariant={handleColorVariant}
                />
              );
            })}
          </div>
        )}

        {/* <div className="w-full"> */}
        {!isIronsCategory && ( // Only render the quantity selector if isIronsCategory is false
          <div className="flex w-full flex-col items-start gap-12">
            <Typography className="text-start text-14 font-normal leading-loose">{'Quantity Selector'}</Typography>
            {/* <Dropdown
      className="h-full rounded-sm"
      value={quantity.toString()}
      items={Array(10)
        .fill(0)
        .map((_, index) => {
          const value = `${index + 1}`;
          return {
            label: value,
            value,
          };
        })}
      onChange={handleQuantityChange}
    /> */}
            {isPersonalized && quantity ? (
              <div className="flex w-full flex-wrap content-center items-center gap-12">
                <div className="flex h-[50px] items-center justify-around rounded-sm border border-[#D9D9D9]">
                  <button
                    className={`flex h-50 w-50 items-center justify-center gap-10 ${
                      quantity === 1 ? 'cursor-not-allowed text-gray-400' : ''
                    }`}
                    onClick={() => setQuantity((prev: number) => Math.max(1, prev - 1))}
                    disabled={quantity === 1}
                  >
                    <MinusIcon />
                  </button>
                  <div className="flex h-50 w-50 items-center justify-center gap-10 ">
                    <span className="text-center font-body text-14 font-normal leading-[100%]">{quantity}</span>
                  </div>

                  <button
                    className={`flex h-50 w-50 items-center justify-center gap-10 ${
                      quantity >= 10 ? 'cursor-not-allowed text-gray-400' : ''
                    }`}
                    onClick={() => setQuantity((prev: number) => Math.min(prev + 1, 10))}
                    disabled={quantity === 10}
                  >
                    <PlusIcon />
                  </button>
                </div>
                {/* Show min quantity message if below min */}
                {isPersonalized && minOrderQuantity && quantity < minOrderQuantity && (
                  <span className="font-body text-14 font-normal leading-[100%] text-[#EB0E0E]">
                    Minimum order quantity is {minOrderQuantity}
                  </span>
                )}
              </div>
            ) : (
              <>
                <div className="flex h-[40px] w-[132px] items-center justify-around gap-2 rounded-md border border-[#686868] xl:w-[42%]">
                  <button
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-lg font-normal ${
                      quantity === 1 ? 'cursor-not-allowed text-gray-400' : 'hover:bg-gray-200'
                    }`}
                    onClick={() => setQuantity((prev: number) => Math.max(1, prev - 1))} // Decrease quantity but not below 1
                    disabled={quantity === 1} // Disable when quantity is 1
                  >
                    -
                  </button>
                  <span className="flex h-8 w-8 items-center justify-center text-lg font-normal">{quantity}</span>
                  <button
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-lg font-normal ${
                      quantity === 10 ? 'cursor-not-allowed text-gray-400' : 'hover:bg-gray-200'
                    }`}
                    onClick={() => setQuantity((prev: any) => Math.min(prev + 1, 10))} // Increase quantity but not above 10
                    disabled={quantity === 10} // Disable when quantity is 10
                  >
                    +
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        {/* </div> */}
        {isPersonalized && (
          <div className="flex flex-col items-start gap-8 self-stretch" data-qa="delivery-date-section">
            {product?.variants?.[0]?.attributes?.personalisedLeadTime && (
              <p className="font-body text-14 font-bold leading-[100%] text-[#1E1E1E]">
                {`Estimated Dispatch: ${getEstimatedDispatchDate(
                  product?.variants?.[0]?.attributes?.personalisedLeadTime,
                )}`}
              </p>
            )}

            {termsAndCondition?.contentBlocks?.length > 0 && (
              <p
                className="cursor-pointer font-body text-14 font-normal leading-[100%] text-[#1E1E1E] underline"
                onClick={() => setOpen(true)}
              >
                {formatProductMessage({ id: 'personalised.termsAndCondition', defaultMessage: 'Terms & Conditions' })}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center">
        {typeof discountPercentage === 'number' && discountPercentage > 0 ? (
          <div className="flex flex-row">
            <div className="flex flex-col items-start gap-y-4">
              {product?.customFitDetails && customFitVariant ? (
                <>
                  <p className={priceClassName}>
                    MRRP {currency === 'GBP' ? '£' : '€'}
                    {discountedMRPrices}
                  </p>
                  <p className="block font-body text-20 font-bold leading-6 text-[#EB0E0E]">{`Now
                   ${currency === 'GBP' ? '£' : '€'}${customFitPrice}`}</p>
                </>
              ) : (
                <>
                  <p className={priceClassName}>
                    MRRP {CurrencyHelpers.formatForCurrency(discountedPrices?.mrrp ?? 0, locale)}
                  </p>
                  <p className="block font-body text-20 font-bold leading-6 text-[#EB0E0E]">
                    {/* Need to Fix **/}
                    {/* Now  {CurrencyHelpers.formatForCurrency(variant.discountedPrice as number, locale)} */}
                    {/* Now {currency === 'GBP' ? '£' : '€'} {((variant?.discountedPrice as any)?.centAmount / 100).toFixed(2)} */}
                    Now {CurrencyHelpers.formatForCurrency(price, locale, currencyCode, fractionDigits)}
                  </p>
                </>
              )}
            </div>
            {/* As of now commentted : since we dont't have this in the Figma design */}
            {/* <span className="ml-20 mt-10 flex h-[26px] min-w-[78px] justify-center self-end rounded-md border border-black p-5 font-body text-14 font-medium leading-[16.4px] text-black">
              Save {Math.round(discountPercentage)}%
            </span> */}
          </div>
        ) : customFitVariant && customFitPrice ? (
          <div className="flex items-center">
            <p className="block font-body text-20 font-bold leading-6">
              {currency === 'GBP' ? '£' : '€'}
              {customFitPrice?.toFixed(2)}
            </p>
          </div>
        ) : (
          <div className="flex items-center">
            <Typography className={`${priceClassName} font-body text-20 font-bold leading-6`}>
              {/* As of now Commentted : we are getting from attributes.standalonePrice */}
              {/* {CurrencyHelpers.formatForCurrency(price?.[0] as number, locale)} */}
              {CurrencyHelpers.formatForCurrency(price, locale, currencyCode, fractionDigits)}
            </Typography>
          </div>
        )}
      </div>

      <Modal
        isOpen={isSizeGuideModalOpen}
        onRequestClose={() => {
          setIsSizeGuideModalOpen(false);
          sizeOverlayContentsRef.current = null;
        }}
      >
        <div className="flex flex-col">
          <div className="mb-20 flex items-end justify-end" onClick={() => setIsSizeGuideModalOpen(false)}>
            <button className="mt-[-1px] text-right font-body text-14 font-medium leading-4">{sale.closeButton}</button>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              x="0px"
              y="0px"
              width="13"
              height="13"
              viewBox="0 0 48 48"
              className="ml-2 cursor-pointer"
            >
              <path fill="#F44336" d="M21.5 4.5H26.501V43.5H21.5z" transform="rotate(45.001 24 24)"></path>
              <path fill="#F44336" d="M21.5 4.5H26.5V43.501H21.5z" transform="rotate(135.008 24 24)"></path>
            </svg>
          </div>
          <div className="mx-10 flex w-full flex-col items-center">
            {product?.brandLogo && (
              <div className="mb-10 w-[100%] max-w-[150px]">
                <img src={BrandLogo} className="w-[100%]" />
              </div>
            )}
            <h2 className="mb-25 font-body text-34 font-bold capitalize leading-[34px]">
              {product?.variants?.[0]?.attributes?.brand} - {isGolfClubProducts ? 'Find your fit' : 'Size Guide'}
            </h2>
            <p className="mb-25 text-sm text-gray-500">{'Note: Information supplied by manufacturer'}</p>

            {/* Check if there are multiple tableSets */}
            {tableSet?.length > 1 ? (
              // Multiple TableSets: Render Accordion for each
              tableSet.map((table: TableSet, index) => {
                const combinedSizeData = `${table.sizeData || ''} ${table.extraSizeData || ''}`.trim();

                return (
                  <AccordionBtn
                    key={index}
                    className="col-span-2 border-b border-neutral-400 md:mt-0"
                    closedSectionTitle={table?.heading || `Size Guide ${index + 1}`}
                    buttonClassName="py-21 font-body text-16 leading-tight text-primary-black pl-0 md:pr-12"
                    panelClassName="pb-20 md:pt-10 md:pb-30 text-primary-black"
                  >
                    <div className="mb-25 w-full overflow-x-auto">
                      <style>
                        {`
                .styled-size-table th, .styled-size-table td {
                  text-align: center;
                  padding: 10px;
                  border-bottom: 1px solid #ddd;
                }
              `}
                      </style>
                      {combinedSizeData ? (
                        <div
                          className="styled-size-table w-[100%]"
                          dangerouslySetInnerHTML={{
                            __html: combinedSizeData,
                          }}
                        />
                      ) : (
                        <p className="text-sm text-gray-500">
                          No size data available.{' '}
                          <a href="/help" className="text-blue-500 underline">
                            Click here to visit our help section.
                          </a>
                        </p>
                      )}
                    </div>
                  </AccordionBtn>
                );
              })
            ) : (
              // Single TableSet: Render without Accordion
              <div className="mb-25 flex w-full flex-col items-center overflow-x-auto">
                <style>
                  {`
          .styled-size-table th, .styled-size-table td {
            text-align: center;
            padding: 10px;
            border-bottom: 1px solid #ddd;
          }
        `}
                </style>
                <h2 className="mb-25 font-body text-22 font-normal leading-[22px]">
                  {product?.sizeGuide?.tableSet?.[0]?.heading}
                </h2>
                {product?.sizeGuide?.tableSet?.[0]?.sizeData || product?.sizeGuide?.tableSet?.[0]?.extraSizeData ? (
                  <div
                    className="styled-size-table w-[100%]"
                    dangerouslySetInnerHTML={{
                      __html: `${product?.sizeGuide?.tableSet?.[0]?.sizeData || ''} ${
                        product?.sizeGuide?.tableSet?.[0]?.extraSizeData || ''
                      }`.trim(),
                    }}
                  />
                ) : (
                  <p className="text-sm text-gray-500">
                    No size data available.{' '}
                    <Link link={'https://help.americangolf.co.uk/hc/en-gb'} className="text-blue-500 underline">
                      Click here to visit our help section.
                    </Link>
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        <p className="text-16 font-normal leading-[19.2px]">{'Note: Information supplied by manufacturer'}</p>
      </Modal>
      {open && (
        <>
          <TermsContent termsAndCondition={termsAndCondition} setOpen={setOpen} modalRef={modalRef} />
        </>
      )}
    </div>
  );
};
export default ProductInformation;
